import dayjs from "@tether-web-portal/dayjs-setup";
import { DeviceType } from "../types/DeviceType";
import { deviceOnlineState } from "./deviceOnline";

interface SampleData {
  timestamp: number | string;
  sample?: {
    co2?: number;
    // installationlocation/id/samples/latest is a number but for the property info api it’s a string
  };
}

/**
 * Thin wrapper over the catalog-driven rule in `deviceOnline.ts`, kept because ten call sites use
 * this name and this shape.
 *
 * The per-type timeout map that used to live here now comes from the device catalog, so the same
 * numbers reach tesseract and tether-mobile instead of each client keeping its own. That map is
 * why: it gained a 6-hour Bove Beco entry in 2026-09 and the two sibling clients never got it.
 *
 * `unknown` (no timestamp at all) collapses to false here — a room with no reading must not claim
 * to be live. Sibling call sites resolve `unknown` the other way for their own reasons, which is
 * exactly why `deviceOnlineState` returns three states and each caller writes its own answer down
 * rather than inheriting a default.
 */
export const isSampleRecent = (sample: SampleData, deviceType?: DeviceType | string) =>
  deviceOnlineState(sample?.timestamp, deviceType) === "online";

export const isSampleRecentWithCO2 = (sample: SampleData, deviceType?: DeviceType | string) => {
  if (!sample?.sample?.co2) return false;
  return isSampleRecent(sample, deviceType);
};

/**
 * Deliberately NOT catalog-driven, and deliberately blind to the device type — do not "finish the
 * job" by routing this through `deviceOnlineState`.
 *
 * It asks a different question from `isSampleRecent`: not "is this device online" but "is this
 * reading worth rendering at all". Callers use it to hide a comfort score, the health summary, and
 * the battery and signal figures behind a reading so old it would mislead. Two weeks is far past any
 * catalog tolerance (those are capped at 36 hours), so every really-old sample is already offline;
 * swapping in a per-type tolerance would collapse "offline" and "not worth showing" into one state
 * and blank those figures for any device that missed a couple of uplinks.
 */
export const isSampleReallyOld = (sample: SampleData) => {
  if (!sample?.timestamp) return false;

  const isOld = sample?.timestamp && !(dayjs().diff(sample?.timestamp, "week") < 2);

  return Boolean(isOld);
};
